import React from 'react';
import {FieldArray, FieldArrayRenderProps} from "formik";
import {FactData} from "../../../types/Fact";
import FactType from "../../../types/FactType";
import IconName from "../../../types/IconName";
import FieldHeader from "../../../components/Form/FieldHeader";
import Icon from "../../../components/Icon";
import Selector from "../../../components/Form/Selector";
import Field from "../../../components/Form/Field";
import {SelectField, TextField} from "../../../components/Form/Form";
import {toTitleCase} from "../../../services/string";
import Button from "../../../components/Button";
import {PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {FactTemplate} from 'types/FactTemplate';
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";

// Helpers ----

const factValueLengths: Record<FactType, number> = {
  [FactType.OPTION]: 0,
  [FactType.SHORT_TEXT]: 250,
  [FactType.MEDIUM_TEXT]: 5000,
  [FactType.LONG_TEXT]: 50000,
}

// Component ----

type FactEditorProps = {
  name: string
  factTemplates?: Array<FactTemplate>
  dark?: boolean
}

function FactEditor({name, factTemplates, dark}: FactEditorProps) {
  return (
    <FieldArray name={name}>
      {({form, push, remove}: FieldArrayRenderProps) => {

        const factList = name
          .split('.')
          .reduce((object, key) => object[key], form.values) as Array<FactData>

        return (
          <div className="lg:col-span-6">
            {/* Fact List */}
            <ul className="grid grid-cols-1 gap-4 lg:grid-cols-6">
              {factList.map((fact, index) => {

                const template = (factTemplates ?? []).find(template => template.id === fact.template_id)
                const factType = template?.type ?? fact.type ?? FactType.SHORT_TEXT
                const factIcon = template?.icon ?? fact.icon ?? IconName.QUESTION_MARK_CIRCLE
                const factName = template?.name ?? fact.name ?? ''
                const options = template?.type === FactType.OPTION ? template.options : []
                const maxLength = factValueLengths[factType]

                return (
                  <li className="mt-4 lg:col-span-3" key={index}>
                    {/* Templated Facts */}
                    {!!fact.template_id &&
                      <>
                        <FieldHeader
                          fieldId={`${name}.${index}.value`}
                          hint={!!maxLength ? `${maxLength.toLocaleString()} chars max` : undefined}
                        >
                          <div className="flex">
                            <Icon name={factIcon} className="h-5 w-5 text-gray-500 mr-1"/>
                            {factName}
                          </div>
                        </FieldHeader>

                        <div className="mt-2">
                          {factType === FactType.OPTION &&
                            <>
                              <Selector
                                items={options.map(option => ({id: option, name: option})) || []}
                                selectedId={fact.value?.toString()}
                                onChange={item => form.setFieldValue(`${name}.${index}.value`, item.id)}
                              />

                              {/* @ts-ignore */}
                              {(!!form.errors?.[name]?.[index]?.value && form.touched?.[name]?.[index]?.value) &&
                                <FieldHelp isError>
                                  {/* @ts-ignore */}
                                  {form.errors?.[name]?.[index].value}
                                </FieldHelp>
                              }
                            </>
                          }

                          {factType !== FactType.OPTION &&
                            <Field
                              as={factType === FactType.SHORT_TEXT ? 'input' : 'textarea'}
                              type={factType === FactType.SHORT_TEXT ? 'text' : undefined}
                              rows={factType !== FactType.SHORT_TEXT ? 4 : undefined}
                              name={`${name}.${index}.value`}
                              id={`${name}.${index}.value`}
                              autoComplete="off"
                              maxLength={maxLength}
                            />
                          }
                        </div>
                      </>
                    }

                    {/* Extra Facts */}
                    {!fact.template_id &&
                      <div className={`rounded-lg p-4 ${dark? 'bg-gray-100' : 'bg-gray-50'}`}>
                        <div className="grid lg:grid-cols-4 gap-2">
                          <div className="lg:col-span-1 flex space-x-2 lg:block">
                            <div className="w-full">
                              <SelectField
                                name={`${name}.${index}.type`}
                                label="Type"
                                options={Object
                                  .values(FactType)
                                  .filter(type => type !== FactType.OPTION)
                                  .map(type => ({id: type, name: toTitleCase(type)}))
                                }
                              />
                            </div>

                            <div className="mt-8 flex-grow-0 lg:hidden">
                              <Button
                                size={Button.sizes.X_SMALL}
                                color={Button.colors.WHITE}
                                icon={TrashIcon}
                                onClick={() => remove(index)}
                              />
                            </div>
                          </div>

                          <div className="lg:col-span-2">
                            <SelectField
                              name={`${name}.${index}.icon`}
                              label="Icon"
                              options={Object
                                .values(IconName)
                                .map(icon => ({id: icon, name: toTitleCase(icon), icon}))
                              }
                            />
                          </div>

                          <div className="hidden lg:col-span-1 lg:flex lg:flex-row-reverse">
                            <div className="mt-8 flex-grow-0">
                              <Button
                                size={Button.sizes.X_SMALL}
                                color={Button.colors.WHITE}
                                icon={TrashIcon}
                                onClick={() => remove(index)}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="mt-2">
                          <TextField
                            name={`${name}.${index}.name`}
                            label="Name *"
                            hint="250 chars max"
                            maxLength={250}
                          />
                        </div>

                        <div className="mt-2">
                          <div className="mt-2">
                            <Field
                              as={factType === FactType.SHORT_TEXT ? 'input' : 'textarea'}
                              type={factType === FactType.SHORT_TEXT ? 'text' : undefined}
                              rows={factType !== FactType.SHORT_TEXT ? 4 : undefined}
                              name={`${name}.${index}.value`}
                              id={`${name}.${index}.value`}
                              autoComplete="off"
                              maxLength={maxLength}
                              label='Value *'
                              hint={fact.type !== FactType.OPTION ? `${maxLength} chars max` : undefined}
                            />
                          </div>
                        </div>
                      </div>
                    }
                  </li>
                )
              })}
            </ul>

            {/* "Add" button */}
            <div className={factList.length? 'mt-4' : ''}>
              <Button
                color={Button.colors.SECONDARY}
                icon={PlusIcon}
                size={Button.sizes.TINY}
                onClick={() => push({type: FactType.SHORT_TEXT, icon: IconName.QUESTION_MARK_CIRCLE, name: '', value: ''})}
              >
                Add Fact
              </Button>
            </div>
          </div>
        )
      }}
    </FieldArray>
  );
}

export default FactEditor;